import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useNotification } from '../../../components/ui/NotificationProvider';

const WebhookConfiguration = ({ 
  initialData = {}, 
  onSave, 
  isLoading = false,
  errors = {}
}) => {
  const { showSuccess, showError } = useNotification();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const [webhookData, setWebhookData] = useState({
    n8nEndpoint: initialData?.n8nEndpoint || '',
    authToken: initialData?.authToken || '',
    isEnabled: Boolean(initialData?.isEnabled),
    hasAuthToken: Boolean(initialData?.hasAuthToken),
    authTokenPreview: initialData?.authTokenPreview || null,
    lastTestedAt: initialData?.lastTestedAt || null,
    lastDeliveryAt: initialData?.lastDeliveryAt || null
  });

  const [showAuthToken, setShowAuthToken] = useState(false);
  const [isFetchingToken, setIsFetchingToken] = useState(false);
  const [tokenDirty, setTokenDirty] = useState(false);

  useEffect(() => {
    setWebhookData(prev => ({
      ...prev,
      n8nEndpoint: initialData?.n8nEndpoint || '',
      authToken: initialData?.authToken || '',
      isEnabled: Boolean(initialData?.isEnabled),
      hasAuthToken: Boolean(initialData?.hasAuthToken),
      authTokenPreview: initialData?.authTokenPreview || null,
      lastTestedAt: initialData?.lastTestedAt || null,
      lastDeliveryAt: initialData?.lastDeliveryAt || null
    }));
    setShowAuthToken(false);
    setTokenDirty(false);
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setWebhookData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'authToken') {
      setTokenDirty(true);
    }
  };

  const generateAuthToken = () => {
    const token = 'n8n_' + Math.random()?.toString(36)?.substring(2) + Date.now()?.toString(36);
    setWebhookData(prev => ({
      ...prev,
      authToken: token,
      hasAuthToken: false,
      authTokenPreview: null
    }));
    setTokenDirty(true);
    setShowAuthToken(true);
  };

  const loadStoredToken = async () => {
    if (!apiBaseUrl || isFetchingToken || tokenDirty || !webhookData?.hasAuthToken) {
      return;
    }

    try {
      setIsFetchingToken(true);
      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/webhook-config?includeToken=true`);
      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'No se pudo obtener el token guardado');
      }

      setWebhookData(prev => ({
        ...prev,
        authToken: result?.data?.authToken || '',
        hasAuthToken: Boolean(result?.data?.authToken),
        authTokenPreview: result?.data?.authTokenPreview || prev.authTokenPreview
      }));
      setTokenDirty(false);
    } catch (error) {
      showError?.(`No se pudo mostrar el token guardado: ${error?.message || error}`);
    } finally {
      setIsFetchingToken(false);
    }
  };

  const toggleAuthTokenVisibility = async () => {
    if (!showAuthToken && webhookData?.hasAuthToken && !webhookData?.authToken && !tokenDirty) {
      await loadStoredToken();
    }
    setShowAuthToken(prev => !prev);
  };

  const testWebhook = async () => {
    if (!apiBaseUrl) {
      showError?.('No se ha configurado la URL base de la API', { title: 'Configuración incompleta' });
      return;
    }

    const bodyPayload = {
      n8nEndpoint: webhookData?.n8nEndpoint,
      isEnabled: webhookData?.isEnabled,
      authToken: tokenDirty ? (webhookData?.authToken?.trim() || '') : undefined
    };

    try {
      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/webhook/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload)
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.error || result?.message || 'Error al probar el webhook');
      }

      setWebhookData(prev => ({
        ...prev,
        lastTestedAt: new Date().toISOString()
      }));

      showSuccess?.('Webhook probado correctamente', { title: 'Prueba exitosa' });
    } catch (error) {
      showError?.('Error al probar el webhook: ' + (error?.message || error), { title: 'Error' });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const payload = {
      n8nEndpoint: webhookData?.n8nEndpoint?.trim() || '',
      isEnabled: Boolean(webhookData?.isEnabled)
    };

    if (tokenDirty) {
      payload.authToken = webhookData?.authToken?.trim() || '';
    }

    await onSave?.(payload);
  };

  const isFormValid = !webhookData?.isEnabled || Boolean(webhookData?.n8nEndpoint?.trim());

  const formatTimestamp = (value) => {
    if (!value) return null;
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleString();
    } catch (_) {
      return null;
    }
  };

  const authStatusLabel = (() => {
    if (tokenDirty) {
      return webhookData?.authToken?.trim() ? 'Configurada (sin guardar)' : 'Pendiente';
    }
    return webhookData?.hasAuthToken ? 'Configurada' : 'Pendiente';
  })();

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
          <Icon name="Webhook" size={20} className="text-secondary" />
        </div>
        <div>
          <h3 className="font-semibold text-text-primary">Configuración de Webhooks</h3>
          <p className="text-sm text-text-secondary">Integración con n8n para automatización</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <Checkbox
            label="Habilitar envío a n8n"
            description="Cuando está activo, todas las notificaciones recibidas se reenviarán al endpoint configurado."
            checked={Boolean(webhookData?.isEnabled)}
            onChange={(e) => handleInputChange('isEnabled', e?.target?.checked)}
          />

          <Input
            label="Endpoint de n8n"
            type="url"
            placeholder="https://tu-instancia-n8n.com/webhook/mercadolibre"
            description="URL del webhook de n8n donde se enviarán los eventos"
            value={webhookData?.n8nEndpoint}
            onChange={(e) => handleInputChange('n8nEndpoint', e?.target?.value)}
            error={errors?.n8nEndpoint}
            required={Boolean(webhookData?.isEnabled)}
          />

          <div className="relative">
            <Input
              label="Token de Autenticación"
              type={showAuthToken ? "text" : "password"}
              placeholder={webhookData?.authTokenPreview ? `Token guardado (${webhookData?.authTokenPreview})` : 'Token para autenticar las solicitudes webhook'}
              description="Token que se enviará en el header Authorization"
              value={webhookData?.authToken}
              onChange={(e) => handleInputChange('authToken', e?.target?.value)}
              error={errors?.authToken}
            />
            <div className="absolute right-3 top-8 flex items-center space-x-1">
              <button
                type="button"
                onClick={toggleAuthTokenVisibility}
                className="p-1 text-text-secondary hover:text-text-primary transition-smooth"
                disabled={isFetchingToken}
              >
                <Icon name={showAuthToken ? "EyeOff" : "Eye"} size={18} />
              </button>
              <button
                type="button"
                onClick={generateAuthToken}
                className="p-1 text-text-secondary hover:text-text-primary transition-smooth"
                title="Generar token automáticamente"
              >
                <Icon name="RefreshCw" size={18} />
              </button>
            </div>
          </div>

          {webhookData?.authTokenPreview && !tokenDirty && !showAuthToken && (
            <p className="text-xs text-text-secondary">
              Token guardado: {webhookData.authTokenPreview}
            </p>
          )}

          {isFetchingToken && (
            <p className="text-xs text-text-secondary">Recuperando token seguro...</p>
          )}
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-text-primary mb-2">Eventos que se enviarán:</p>
              <ul className="list-disc list-inside space-y-1 text-text-secondary">
                <li>Inicio de carga masiva de productos</li>
                <li>Progreso de publicación (cada 10 productos)</li>
                <li>Finalización de carga (exitosa o con errores)</li>
                <li>Errores críticos durante el proceso</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-warning mb-1">Formato del Payload</p>
              <pre className="text-xs bg-background rounded p-2 mt-2 overflow-x-auto">
{`{
  "event": "upload_progress",
  "timestamp": "2025-01-02T03:20:25.778Z",
  "data": {
    "total": 100,
    "processed": 45,
    "errors": 2,
    "status": "processing"
  }
}`}
              </pre>
            </div>
          </div>
        </div>

        <div className="bg-muted border border-border rounded-lg p-4">
          <h4 className="text-sm font-semibold text-text-primary mb-2">Estado del webhook</h4>
          <div className="grid gap-1 text-xs text-text-secondary sm:grid-cols-2">
            <span>Estado: {webhookData?.isEnabled ? 'Activo' : 'Desactivado'}</span>
            <span>Última prueba: {formatTimestamp(webhookData?.lastTestedAt) || 'Nunca'}</span>
            <span>Última entrega: {formatTimestamp(webhookData?.lastDeliveryAt) || 'Sin registros'}</span>
            <span>Autenticación: {authStatusLabel}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            type="submit"
            variant="default"
            loading={isLoading}
            disabled={!isFormValid}
            iconName="Save"
            iconPosition="left"
            className="flex-1"
          >
            Guardar Configuración
          </Button>
          
          <Button
            type="button"
            variant="outline"
            onClick={testWebhook}
            disabled={!webhookData?.n8nEndpoint || isLoading}
            iconName="Send"
            iconPosition="left"
          >
            Probar Webhook
          </Button>
        </div>
      </form>
    </div>
  );
};

export default WebhookConfiguration;